import type { TargetFile } from './targetFile.js';
import type { ResourceStatistics } from '../../../Assembler/dist/model/index.js';

/**
 * Stores loaded template data (immutable after Load) for the NormalJson engine.
 * Separates loaded data from per-merge mutable state in the loader.
 */
export class TemplateStoreJson {
    // Cache: AppSite -> Map<templateName, TargetFile>
    private _htmlJsonCache: Map<string, Map<string, TargetFile>> = new Map();
    private _dataJsonCache: Map<string, Map<string, TargetFile>> = new Map();
    private _resourceStatsCache: Map<string, ResourceStatistics> = new Map();

    // #region HtmlJson

    hasHtmlJsonAppSite(appSite: string): boolean {
        const htmlTemplates = this._htmlJsonCache.get(appSite);
        return htmlTemplates !== undefined;
    }

    tryGetHtmlJsonTemplates(appSite: string): Map<string, TargetFile> | undefined {
        const htmlTemplates = this._htmlJsonCache.get(appSite);
        if (htmlTemplates) {
            return htmlTemplates;
        }

        return undefined;
    }

    getHtmlJsonTemplates(appSite: string): Map<string, TargetFile> | undefined {
        const htmlTemplates = this._htmlJsonCache.get(appSite);
        if (htmlTemplates) {
            return htmlTemplates;
        }

        return undefined;
    }

    ensureHtmlJsonAppSite(appSite: string): void {
        if (!this._htmlJsonCache.has(appSite)) {
            this._htmlJsonCache.set(appSite, new Map());
        }
    }

    setHtmlJsonTemplates(appSite: string, templates: Map<string, TargetFile>): void {
        this._htmlJsonCache.set(appSite, templates);
    }

    setHtmlJsonTemplate(appSite: string, templateName: string, targetFile: TargetFile): void {
        this.ensureHtmlJsonAppSite(appSite);
        const htmlTemplates = this._htmlJsonCache.get(appSite)!;
        htmlTemplates.set(templateName, targetFile);
    }

    getHtmlJsonAppSites(): IterableIterator<string> {
        const appSites = this._htmlJsonCache.keys();
        return appSites;
    }

    getHtmlJsonCount(appSite: string): number {
        const templates = this._htmlJsonCache.get(appSite);
        return templates ? templates.size : 0;
    }

    // #endregion

    // #region DataJson

    hasDataJsonAppSite(appSite: string): boolean {
        const dataTemplates = this._dataJsonCache.get(appSite);
        return dataTemplates !== undefined;
    }

    tryGetDataJsonTemplates(appSite: string): Map<string, TargetFile> | undefined {
        const dataTemplates = this._dataJsonCache.get(appSite);
        if (dataTemplates) {
            return dataTemplates;
        }

        return undefined;
    }

    getDataJsonTemplates(appSite: string): Map<string, TargetFile> | undefined {
        const dataTemplates = this._dataJsonCache.get(appSite);
        if (dataTemplates) {
            return dataTemplates;
        }

        return undefined;
    }

    ensureDataJsonAppSite(appSite: string): void {
        if (!this._dataJsonCache.has(appSite)) {
            this._dataJsonCache.set(appSite, new Map());
        }
    }

    setDataJsonTemplates(appSite: string, templates: Map<string, TargetFile>): void {
        this._dataJsonCache.set(appSite, templates);
    }

    setDataJsonTemplate(appSite: string, templateName: string, targetFile: TargetFile): void {
        this.ensureDataJsonAppSite(appSite);
        const dataTemplates = this._dataJsonCache.get(appSite)!;
        dataTemplates.set(templateName, targetFile);
    }

    getDataJsonAppSites(): IterableIterator<string> {
        const appSites = this._dataJsonCache.keys();
        return appSites;
    }

    getDataJsonCount(appSite: string): number {
        const templates = this._dataJsonCache.get(appSite);
        return templates ? templates.size : 0;
    }

    // #endregion

    // #region ResourceStats

    tryGetResourceStats(appSite: string): ResourceStatistics | undefined {
        const resourceStats = this._resourceStatsCache.get(appSite);
        if (resourceStats) {
            return resourceStats;
        }

        return undefined;
    }

    setResourceStats(appSite: string, stats: ResourceStatistics): void {
        this._resourceStatsCache.set(appSite, stats);
    }

    // #endregion

    clear(): void {
        this._htmlJsonCache.clear();
        this._dataJsonCache.clear();
        this._resourceStatsCache.clear();
    }
}
